// ─────────────────────────────────────────────────────────────────────────────
// workbook.ts — the Sales Intelligence Workbook: its identity, and the Excel
// palette it is drawn in. Every value here is SAMPLED from the design HTML's
// stylesheet (docs/growth_mission_control/, #sales-workbook), not chosen.
//
// ── ONE FILE, TWO TABS ──────────────────────────────────────────────────────
// sales_intelligence_master.xlsx is ONE file. Marketing writes into it at the
// handoff boundary; Sales reads and works the same file. When Marketing adds a
// row it appears in Sales' workbook because IT IS THE SAME FILE — there is no
// copy, no second store and NOTHING TO SYNC. So this module is a single set of
// constants consumed by a single renderer, declared once in surface.ts and
// referenced by both tabs. Two copies of any of it would be the beginning of the
// second store the design says does not exist.
//
// ── WHY THE SHEET BODY IS LIGHT, AND WHY THAT IS NOT A REVIEW BLOCK ─────────
// The dark theme is the base and is never overridden — for PAGE CHROME. This is
// not chrome: it is a spreadsheet, and the design draws it as one (#f7faf8 on
// #14231e), the same way it draws the script sheet as a light document. The
// owner ruling of 2026-08-01 makes the design final on every visual matter, and
// the light surface here is the design's, deliberately, so the workbook reads as
// Excel rather than as a panel. Do not "fix" it to dark.
//
// ── NO PULSE HOOK, AND NO LEVER FOR ONE ─────────────────────────────────────
// A workbook change will one day fire the 09:00 daily pulse to the Sales Agent so
// Sales knows the file moved. NOT THIS ROUND, by owner ruling. There is therefore
// no onChange, no callback prop, no emitter and no disabled flag standing ready —
// a lever left in place is the build happening early with a switch taped over it.
// ─────────────────────────────────────────────────────────────────────────────

/** The file. Transcribed from the design's toolbar, not constructed. */
export const WORKBOOK_FILE = {
  name: 'sales_intelligence_master.xlsx',
  path: '/var/lib/rexy/sales/workbooks/',
  icon: 'X',
} as const;

/** The design's formula row: the name box, the fx marker, and the formula. The
 *  weights are the workbook's scoring rule as the design states it — structure,
 *  never a computation performed here. This surface evaluates nothing. */
export const WORKBOOK_FORMULA = {
  nameBox: 'H4',
  fx: 'fx',
  text: '=ROUND((Pain*0.30)+(ReceptionGap*0.25)+(Affordability*0.20)'
      + '+(PhoneDependence*0.15)+(Evidence*0.10),2)',
} as const;

/** The four toolbar actions, in the design's order.
 *
 *  THEY ARE INERT AND VISIBLY SO. Each one is an ACTION and would go to the box
 *  like every other button — and the workbook backend does not exist, so there is
 *  no endpoint for any of them. A live-looking button that posts nowhere is worse
 *  than an absent one (the Regions & Maps rule), but these are named in the design
 *  as part of what makes the shell read as Excel. So they are RENDERED and
 *  DISABLED, carrying the reason, rather than either faked or dropped. */
export const WORKBOOK_ACTIONS = ['↻ Refresh', '⤓ Export', '＋ Row', '⚙ Columns'] as const;

/** The status bar. `kind` is what makes each cell honest:
 *    static — a fact about the design's own engine; true with no backend.
 *    data   — a MEASUREMENT or a live claim. Absent renders '—', never the
 *             design's sample word. "UI mirror: synced" asserts a live state;
 *             printing it against no backend would be the surface claiming a
 *             sync that cannot exist yet. */
export const WORKBOOK_STATUS: { key: string; label?: string; kind: 'static' | 'data'; value?: string }[] = [
  { key: 'state',    kind: 'data' },
  { key: 'rows',     label: 'Rows',             kind: 'data' },
  { key: 'sectors',  label: 'Qualified sectors', kind: 'data' },
  { key: 'engine',   label: 'Workbook engine',   kind: 'static', value: 'Rexy XLSX / openpyxl' },
  { key: 'mirror',   label: 'UI mirror',         kind: 'data' },
];

/** Columns rendered with the design's cell-score styling. Matched by the column
 *  label the sheet declares, so a sheet that renames a column stops scoring it
 *  rather than scoring the wrong one silently. */
export const SCORE_COLUMNS = new Set(['E · Fit', 'Total']);

/** The Excel palette — every value lifted from the design's stylesheet. */
export const WB = {
  shellBorder: 'rgba(96,210,164,.30)',
  shellBg: '#07100f',
  toolbarBg: 'linear-gradient(90deg,#0a3325,#0c1d21)',
  toolbarLine: 'rgba(96,210,164,.25)',
  iconBg: '#167d50',
  fileSub: '#89aa9d',
  actionBorder: 'rgba(255,255,255,.10)',
  actionBg: 'rgba(255,255,255,.055)',
  actionText: '#dff7ef',
  formulaBg: '#0b1516',
  divider: 'rgba(255,255,255,.08)',
  nameBox: '#d9eee8',
  fxText: '#7cbda7',
  formulaText: '#b9d7ce',
  tabsBg: '#081213',
  tabBg: '#14201f',
  tabText: '#8fb2a6',
  tabActiveBg: '#1a4f38',
  tabActiveText: '#eafff7',
  sheetBg: '#f7faf8',
  sheetText: '#14231e',
  thBg: '#e6eee9',
  thText: '#20372e',
  thRight: '#c6d3cb',
  thBottom: '#b9c9bf',
  tdText: '#1f2b27',
  tdBg: '#ffffff',
  tdAlt: '#f5f8f6',
  tdLine: '#d8e0db',
  rowheadBg: '#e7ede9',
  rowheadText: '#60776d',
  scoreBg: '#dff7e9',
  scoreText: '#12613f',
  statusBg: '#0a3325',
  statusText: '#a9d8c4',
} as const;
